// src/features/reservations/components/ReservationsPage.tsx
//
// «Reservas» (mockup 03) — the drill-down list, under Recepción / Dirección.
// Self-managed data (like Events/Error Monitor): own state, own fetch against
// GET /api/v1/ui/reservations, filters mirrored into the URL query string so
// a filtered view can be shared (Decision 6/9, unit C of wave 2 UI). The
// datagrid itself reuses CustomDatagrid via react-admin's `useList` (client
// wrapper around an already server-fetched, server-paginated page).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { ClearRounded as ClearIcon } from '@mui/icons-material';
import {
  FunctionField,
  ListContextProvider,
  useDataProvider,
  useList,
  useTranslate,
} from 'react-admin';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { DASH, formatCount } from '@/lib/analyticsFormat';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import CustomDatagrid from '@/components/common/CustomDatagrid';
import { EmptyState } from '@/components/common';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useCsvExport, type CsvColumn } from '@/hooks/useCsvExport';
import { getCurrentUser } from '@/utils/auth';
import { hasResourcePermission } from '@/config/resourceConfig';
import { getChannelMeta } from '@/design/registry/channelRegistry';
import { StateChip } from './StateChip';
import {
  displayIdentifier,
  formatIdentification,
  formatKey,
  formatPayment,
  formatRowCharged,
  rowChargedCsv,
  formatPreCheckIn,
} from '../lib/reservationFormat';
import {
  DEFAULT_FILTERS,
  KEY_LABEL_KEYS,
  PAYMENT_LABEL_KEYS,
  PRE_CHECK_IN_LABEL_KEYS,
  chargedSummary,
  filtersFromSearchParams,
  filtersToSearchParams,
  type FiltersState,
} from '../lib/reservationFilters';
import type {
  LiveChannel,
  ReservationPrimaryState,
  ReservationsResponse,
  ReservationState,
} from '@/types/reservationState';

const STATE_OPTIONS: ReservationPrimaryState[] = [
  'notStarted', 'preCheckInPartial', 'preCheckInDone', 'arrivesToday',
  'liveKiosk', 'liveMobileKiosk', 'liveAirLobby', 'needsHelp',
  'checkedInKiosk', 'checkedInMobileKiosk', 'checkedInAirLobby',
  'keyDelivered', 'kioskLeftMidway', 'noRecord', 'inHouse', 'departed',
];

const CHANNEL_OPTIONS: LiveChannel[] = ['Online', 'Kiosk', 'MobileKiosk', 'AirLobby'];

/** Largest page the reservations endpoint serves (it rejects more than 100). */
const EXPORT_PAGE_SIZE = 100;


/** `2026-09-23` → «23/09/2026». */
const formatFilterDate = (value: string): string => {
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};


const activeFilterCount = (filters: FiltersState): number =>
  Object.entries(filters).filter(([key, value]) => {
    if (key === 'axis') return value !== 'arrival';
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return value;
    return Boolean(value);
  }).length;

/**
 * Filter fields rendered inside SectionHeader's own self-managed Popover
 * (`filterContent` prop, no `useRaFilters`) — SectionHeader owns the
 * open/anchor state and the active-count badge here, so these fields are a
 * plain fragment, not wrapped in RaFilterPopover/SelfFilterPopover (those
 * expect an ambient FilterPopoverProvider, which this self-managed page does
 * not use).
 */
const ReservationsFilterFields: React.FC<{
  filters: FiltersState;
  onChange: (patch: Partial<FiltersState>) => void;
}> = ({ filters, onChange }) => {
  const translate = useTranslate();

  return (
    <>
      <TextField
        size="small"
        select
        label={translate('resources.reservations.filters.axis')}
        value={filters.axis}
        onChange={(e) => onChange({ axis: e.target.value as 'arrival' | 'activity' })}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="arrival">{translate('resources.reservations.filters.axisArrival')}</MenuItem>
        <MenuItem value="activity">{translate('resources.reservations.filters.axisActivity')}</MenuItem>
      </TextField>
      <TextField
        size="small"
        type="date"
        label={translate('resources.reservations.filters.from')}
        value={filters.from}
        onChange={(e) => onChange({ from: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        type="date"
        label={translate('resources.reservations.filters.to')}
        value={filters.to}
        onChange={(e) => onChange({ to: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        size="small"
        select
        SelectProps={{
          multiple: true,
          renderValue: (value) => (value as ReservationPrimaryState[]).map((s) => translate(`resources.reservationStates.${s}`)).join(', '),
        }}
        label={translate('resources.reservations.filters.state')}
        value={filters.state}
        onChange={(e) => onChange({ state: e.target.value as unknown as ReservationPrimaryState[] })}
        sx={{ minWidth: 180 }}
      >
        {STATE_OPTIONS.map((s) => (
          <MenuItem key={s} value={s}>{translate(`resources.reservationStates.${s}`)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        select
        SelectProps={{
          multiple: true,
          renderValue: (value) => (value as LiveChannel[]).map((c) => translate(getChannelMeta(c).labelKey)).join(', '),
        }}
        label={translate('resources.reservations.filters.channelUsed')}
        value={filters.channelUsed}
        onChange={(e) => onChange({ channelUsed: e.target.value as unknown as LiveChannel[] })}
        sx={{ minWidth: 160 }}
      >
        {CHANNEL_OPTIONS.map((c) => (
          <MenuItem key={c} value={c}>{translate(getChannelMeta(c).labelKey)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        select
        label={translate('resources.reservations.filters.preCheckIn')}
        value={filters.preCheckIn}
        onChange={(e) => onChange({ preCheckIn: e.target.value as FiltersState['preCheckIn'] })}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">{translate('common.all')}</MenuItem>
        {Object.entries(PRE_CHECK_IN_LABEL_KEYS).map(([value, labelKey]) => (
          <MenuItem key={value} value={value}>{translate(labelKey)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        select
        label={translate('resources.reservations.filters.payment')}
        value={filters.payment}
        onChange={(e) => onChange({ payment: e.target.value as FiltersState['payment'] })}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">{translate('common.all')}</MenuItem>
        {Object.entries(PAYMENT_LABEL_KEYS).map(([value, labelKey]) => (
          <MenuItem key={value} value={value}>{translate(labelKey)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        select
        label={translate('resources.reservations.filters.key')}
        value={filters.key}
        onChange={(e) => onChange({ key: e.target.value as FiltersState['key'] })}
        sx={{ minWidth: 130 }}
      >
        <MenuItem value="">{translate('common.all')}</MenuItem>
        {Object.entries(KEY_LABEL_KEYS).map(([value, labelKey]) => (
          <MenuItem key={value} value={value}>{translate(labelKey)}</MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label={translate('resources.reservations.filters.origin')}
        value={filters.origin}
        onChange={(e) => onChange({ origin: e.target.value })}
        sx={{ minWidth: 130 }}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={filters.kioskLeftMidway} onChange={(e) => onChange({ kioskLeftMidway: e.target.checked })} />}
        label={translate('resources.reservations.filters.kioskLeftMidway')}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={filters.needsHelp} onChange={(e) => onChange({ needsHelp: e.target.checked })} />}
        label={translate('resources.reservations.filters.needsHelp')}
      />
      <FormControlLabel
        control={<Checkbox size="small" checked={filters.isWalkIn} onChange={(e) => onChange({ isWalkIn: e.target.checked })} />}
        label={translate('resources.reservations.filters.isWalkIn')}
      />
    </>
  );
};

const ReservationsContent: React.FC = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getCurrentUser();
  const isReceptionist = user?.role === 'Receptionist';

  const [filters, setFilters] = useState<FiltersState>(() => filtersFromSearchParams(searchParams));
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [response, setResponse] = useState<ReservationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.search);

  const updateFilters = useCallback(
    (patch: Partial<FiltersState>) => {
      setFilters((prev) => {
        const next = { ...prev, ...patch };
        setSearchParams(filtersToSearchParams(next), { replace: true });
        return next;
      });
      setPage(0);
    },
    [setSearchParams]
  );

  // Debounced inline search -> filters.search.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchDraft !== filters.search) updateFilters({ search: searchDraft });
    }, 350);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // The query the list and the export share: every filter, no page.
  const query = useMemo(
    () => ({
      ...scope.scopeParams,
      axis: filters.axis,
      from: filters.from || undefined,
      to: filters.to || undefined,
      state: filters.state.length ? filters.state : undefined,
      channelUsed: filters.channelUsed.length ? filters.channelUsed : undefined,
      preCheckIn: filters.preCheckIn || undefined,
      payment: filters.payment || undefined,
      key: filters.key || undefined,
      kioskLeftMidway: filters.kioskLeftMidway || undefined,
      needsHelp: filters.needsHelp || undefined,
      isWalkIn: filters.isWalkIn || undefined,
      origin: filters.origin || undefined,
      search: filters.search || undefined,
    }),
    [scope.scopeParams, filters]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await dataProvider.getReservations({
        ...query,
        page: page + 1,
        pageSize,
      });
      setResponse(data);
    } catch {
      setResponse(null);
      setError(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, query, page, pageSize]);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const rows = response?.data ?? [];
  const listContext = useList({ data: rows, isLoading: loading, perPage: Math.max(rows.length, 1) });

  const exportCsv = useCsvExport({ page: 'reservations' });
  const canExport = hasResourcePermission('reservations', 'export');

  const activeChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (filters.axis !== 'arrival') {
      chips.push({ key: 'axis', label: translate('resources.reservations.activeFilters.axis', { value: translate('resources.reservations.filters.axisActivity') }), onRemove: () => updateFilters({ axis: 'arrival' }) });
    }
    if (filters.from) chips.push({ key: 'from', label: translate('resources.reservations.activeFilters.from', { value: formatFilterDate(filters.from) }), onRemove: () => updateFilters({ from: '' }) });
    if (filters.to) chips.push({ key: 'to', label: translate('resources.reservations.activeFilters.to', { value: formatFilterDate(filters.to) }), onRemove: () => updateFilters({ to: '' }) });
    if (filters.state.length) chips.push({ key: 'state', label: translate('resources.reservations.activeFilters.state', { value: filters.state.map((s) => translate(`resources.reservationStates.${s}`)).join(', ') }), onRemove: () => updateFilters({ state: [] }) });
    if (filters.channelUsed.length) chips.push({ key: 'channelUsed', label: translate('resources.reservations.activeFilters.channelUsed', { value: filters.channelUsed.map((c) => translate(getChannelMeta(c).labelKey)).join(', ') }), onRemove: () => updateFilters({ channelUsed: [] }) });
    if (filters.preCheckIn) chips.push({ key: 'preCheckIn', label: translate('resources.reservations.activeFilters.preCheckIn', { value: translate(PRE_CHECK_IN_LABEL_KEYS[filters.preCheckIn]) }), onRemove: () => updateFilters({ preCheckIn: '' }) });
    if (filters.payment) chips.push({ key: 'payment', label: translate('resources.reservations.activeFilters.payment', { value: translate(PAYMENT_LABEL_KEYS[filters.payment]) }), onRemove: () => updateFilters({ payment: '' }) });
    if (filters.key) chips.push({ key: 'key', label: translate('resources.reservations.activeFilters.key', { value: translate(KEY_LABEL_KEYS[filters.key]) }), onRemove: () => updateFilters({ key: '' }) });
    if (filters.kioskLeftMidway) chips.push({ key: 'kioskLeftMidway', label: translate('resources.reservations.activeFilters.kioskLeftMidway'), onRemove: () => updateFilters({ kioskLeftMidway: false }) });
    if (filters.needsHelp) chips.push({ key: 'needsHelp', label: translate('resources.reservations.activeFilters.needsHelp'), onRemove: () => updateFilters({ needsHelp: false }) });
    if (filters.isWalkIn) chips.push({ key: 'isWalkIn', label: translate('resources.reservations.activeFilters.isWalkIn'), onRemove: () => updateFilters({ isWalkIn: false }) });
    if (filters.origin) chips.push({ key: 'origin', label: translate('resources.reservations.activeFilters.origin', { value: filters.origin }), onRemove: () => updateFilters({ origin: '' }) });
    return chips;
  }, [filters, translate, updateFilters]);

  const clearAllFilters = useCallback(() => {
    setSearchDraft('');
    updateFilters(DEFAULT_FILTERS);
  }, [updateFilters]);

  const locale = translate('common.locale') === 'en' ? enUS : es;

  // «Exportar» writes the whole filtered set, not the visible page: every page
  // is read at the largest size the endpoint accepts, with every column the
  // list and the detail show, formatted as on screen.
  const handleExport = useCallback(async () => {
    const all: ReservationState[] = [];
    for (let exportPage = 1; ; exportPage += 1) {
      const { data } = await dataProvider.getReservations({ ...query, page: exportPage, pageSize: EXPORT_PAGE_SIZE });
      const batch: ReservationState[] = data?.data ?? [];
      all.push(...batch);
      const total = data?.pagination?.totalItems ?? all.length;
      if (batch.length < EXPORT_PAGE_SIZE || all.length >= total) break;
    }
    const localeCode = translate('common.locale');
    const date = (iso: string | null) => (iso ? format(new Date(iso), 'dd/MM/yyyy', { locale }) : '');
    const dateTime = (iso: string | null) => (iso ? format(new Date(iso), 'dd/MM/yyyy HH:mm', { locale }) : '');
    // A file is read later: the pre-check-in is its state and the date it was
    // done, never «hace 2 días», which is only true on the day it was exported.
    const preCheckInState = (r: ReservationState) => {
      if (r.preCheckIn.state === 'done') return translate('resources.arrivals.preCheckIn.done');
      if (r.preCheckIn.state === 'started') return formatPreCheckIn(r.preCheckIn, translate, localeCode);
      return '';
    };
    const columns: CsvColumn<ReservationState>[] = [
      { labelKey: 'resources.reservations.export.reservationNumber', value: (r) => r.reservationNumber },
      { labelKey: 'resources.reservations.columns.arrival', value: (r) => date(r.arrivalDate) },
      { labelKey: 'resources.reservations.columns.departure', value: (r) => date(r.departureDate) },
      { labelKey: 'resources.reservations.columns.guest', value: (r) => displayIdentifier(r) },
      { labelKey: 'resources.reservations.export.room', value: (r) => r.roomNumber ?? '' },
      { labelKey: 'resources.reservations.columns.hotel', value: (r) => r.hotelName ?? '' },
      { labelKey: 'resources.reservations.columns.state', value: (r) => translate(`resources.reservationStates.${r.state}`) },
      { labelKey: 'resources.reservations.columns.preCheckIn', value: preCheckInState },
      { labelKey: 'resources.reservations.export.preCheckInDoneAt', value: (r) => (r.preCheckIn.state === 'done' ? dateTime(r.preCheckIn.doneAt) : '') },
      {
        labelKey: 'resources.reservations.columns.checkIn',
        value: (r) =>
          r.checkIn.completedAt
            ? r.checkIn.channel
              ? translate(getChannelMeta(r.checkIn.channel).labelKey)
              : translate('resources.reservations.checkIn.reception')
            : '',
      },
      { labelKey: 'resources.reservations.export.identification', value: (r) => formatIdentification(r.identification.identified, r.identification.total) },
      { labelKey: 'resources.reservations.export.payment', value: (r) => formatPayment(r.payment, translate) },
      ...(isReceptionist
        ? []
        : [
            // The amount as a number and its currency beside it, so a spreadsheet adds it up.
            { labelKey: 'resources.reservations.columns.charged', value: (r: ReservationState) => rowChargedCsv(r.payment).amount },
            { labelKey: 'resources.reservations.export.currency', value: (r: ReservationState) => rowChargedCsv(r.payment).currency },
          ]),
      { labelKey: 'resources.reservations.columns.key', value: (r) => formatKey(r.key, translate) },
    ];
    await exportCsv(all, columns, { from: filters.from, to: filters.to });
  }, [dataProvider, query, exportCsv, translate, locale, isReceptionist, filters.from, filters.to]);

  return (
    <Box>
      <SectionHeader manualScreen="reservations"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <TextField
              size="small"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              placeholder={translate('resources.reservations.filters.search')}
              sx={{ minWidth: { xs: '100%', sm: 300 } }}
            />
          </Box>
        }
        helpConfig={{
          titleKey: 'resources.reservations.help.title',
          descriptionKey: 'resources.reservations.help.description',
          featureKeys: [
            'resources.reservations.help.features.urlFilters',
            'resources.reservations.help.features.rowClick',
            'resources.reservations.help.features.summary',
            'resources.reservations.help.features.charged',
          ],
          tipKeys: [
            'resources.reservations.help.tips.search',
            'resources.reservations.help.tips.export',
          ],
        }}
        filterContent={<ReservationsFilterFields filters={filters} onChange={updateFilters} />}
        activeFilterCount={activeFilterCount(filters)}
        showExport={canExport}
        onExportClick={handleExport}
        onRefresh={() => void fetchRows()}
        refreshing={loading}
      />

      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1 }}>
        {activeChips.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
            {activeChips.map((chip) => (
              <Chip key={chip.key} size="small" label={chip.label} onDelete={chip.onRemove} deleteIcon={<ClearIcon sx={{ fontSize: 14 }} />} />
            ))}
          </Stack>
        )}

        {response?.summary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {translate('resources.reservations.summary.count', { count: formatCount(response.summary.count) })}
            {' · '}
            {translate('resources.reservations.summary.checkedIn', { count: formatCount(response.summary.checkedIn) })}
            {' · '}
            {translate('resources.reservations.summary.preCheckInDone', { count: formatCount(response.summary.preCheckInDone) })}
            {!isReceptionist && chargedSummary(response.summary) && (
              <>{' · '}{translate('resources.reservations.summary.charged', { amount: chargedSummary(response.summary) })}</>
            )}
          </Typography>
        )}

        {error ? (
          <FetchErrorBanner onRetry={() => void fetchRows()} sx={{ mb: 0 }} />
        ) : !loading && rows.length === 0 ? (
          <EmptyState
            message={translate('resources.reservations.emptyState.title')}
            description={translate('resources.reservations.emptyState.description')}
            showAction={activeFilterCount(filters) > 0}
            actionText={translate('resources.reservations.filters.clear')}
            actionIcon={<ClearIcon />}
            onAction={clearAllFilters}
          />
        ) : (
          <ListContextProvider value={listContext}>
            <CustomDatagrid resource="reservations" rowClick={(id) => { navigate(`/reservations/${id}`); return false; }}>
              <FunctionField
                label={translate('resources.reservations.columns.arrival')}
                render={(r: ReservationState) => (r.arrivalDate ? format(new Date(r.arrivalDate), 'dd/MM', { locale }) : '—')}
              />
              <FunctionField
                label={translate('resources.reservations.columns.departure')}
                render={(r: ReservationState) => (r.departureDate ? format(new Date(r.departureDate), 'dd/MM', { locale }) : '—')}
              />
              <FunctionField
                label={translate('resources.reservations.columns.guest')}
                render={(r: ReservationState) => displayIdentifier(r)}
              />
              <FunctionField
                label={translate('resources.reservations.columns.room')}
                render={(r: ReservationState) => r.roomNumber ?? '—'}
              />
              <FunctionField
                label={translate('resources.reservations.columns.hotel')}
                render={(r: ReservationState) => r.hotelName ?? '—'}
              />
              <FunctionField
                label={translate('resources.reservations.columns.preCheckIn')}
                render={(r: ReservationState) => formatPreCheckIn(r.preCheckIn, translate, translate('common.locale'))}
              />
              <FunctionField
                label={translate('resources.reservations.columns.checkIn')}
                render={(r: ReservationState) =>
                  r.checkIn.completedAt
                    ? (r.checkIn.channel ? translate(getChannelMeta(r.checkIn.channel).labelKey) : translate('resources.reservations.checkIn.reception'))
                    : translate('resources.reservations.checkIn.none')
                }
              />
              <FunctionField
                label={translate('resources.reservations.columns.key')}
                render={(r: ReservationState) => formatKey(r.key, translate)}
              />
              {!isReceptionist && (
                <FunctionField
                  label={translate('resources.reservations.columns.charged')}
                  render={(r: ReservationState) => formatRowCharged(r.payment)}
                />
              )}
              <FunctionField
                label={translate('resources.reservations.columns.state')}
                render={(r: ReservationState) => <StateChip state={r.state} />}
              />
            </CustomDatagrid>
          </ListContextProvider>
        )}

        <TablePagination
          component="div"
          count={response?.pagination.totalItems ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Box>
  );
};

export const ReservationsPage: React.FC = () => <ReservationsContent />;

export default ReservationsPage;
